import { compressSnapshot, decompressSnapshot } from '../bookmarkCompression.js';

/**
 * RestoreBookmarkCommand
 *
 * Self-contained: both before and after states are stored as independent
 * compressed copies. The command never references the bookmark store, so it
 * remains fully functional even after the originating bookmark is deleted.
 */
export default class RestoreBookmarkCommand {
    /**
     * @param {string} bookmarkName   Display label for the history panel.
     * @param {string} rawBefore      Raw (uncompressed) design snapshot BEFORE restore.
     * @param {string} rawAfter       Raw (uncompressed) bookmark snapshot to restore to.
     * @param {Function} restoreFn    Async function that applies a raw snapshot to the canvas.
     */
    constructor(bookmarkName, rawBefore, rawAfter, restoreFn) {
        this.id          = crypto.randomUUID();
        this.timestamp   = Date.now();
        this.description = 'Restore: ' + bookmarkName;
        this.category    = 'structure';

        // Compress both snapshots immediately — independent copies, no bookmark reference
        const before = compressSnapshot(rawBefore);
        const after  = compressSnapshot(rawAfter);
        this._compressedBefore = before.compressedSnapshot;
        this._compressedAfter  = after.compressedSnapshot;

        // restoreFn is provided by App.vue; it has access to the layer, importer,
        // Vue reactivity etc. The command stores it as a plain function ref.
        this._restoreFn = restoreFn;
    }

    async execute() {
        const snapshot = decompressSnapshot(this._compressedAfter);
        await this._restoreFn(snapshot);
    }

    async undo() {
        const snapshot = decompressSnapshot(this._compressedBefore);
        await this._restoreFn(snapshot);
    }

    toJSON() {
        return {
            type: 'RestoreBookmark',
            id: this.id,
            timestamp: this.timestamp,
            description: this.description,
        };
    }
}
